/** Aux Info Sync — launch page. Pick tenant → integration (from the
 *  tenant's CONFIGURED accounts — auth is a prerequisite the restech setup
 *  owns) → optional window/accounts, then start. Navigates straight to the
 *  review page; the grid fills once collect + analyze finish.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as auxSyncApi from '@/api/auxSync';
import { HelpLink } from '@/components/HelpLink';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { getTenantScope } from '@/scope/tenantScope';

export function AuxSyncPage() {
  const { show } = useSnackbar();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState<TenantPickerValue | null>(() => getTenantScope());
  const [integration, setIntegration] = useState<string | null>(null);
  /** null = all configured accounts (the default). */
  const [accountsSel, setAccountsSel] = useState<auxSyncApi.AuxSyncAccount[] | null>(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sampleLimit, setSampleLimit] = useState('');
  const [useAi, setUseAi] = useState(false);
  const [fetchContent, setFetchContent] = useState(false);

  const contextQuery = useQuery({
    queryKey: ['aux-sync', 'launch-context', tenant?.id],
    queryFn: () => auxSyncApi.launchContext(tenant!.id),
    enabled: Boolean(tenant),
  });
  const context = contextQuery.data;
  const integrations = context?.integrations ?? [];
  const selected = integrations.find((i) => i.integration === integration) ?? null;

  // A subset only when the user actually narrowed it; omitting the field
  // means "all configured accounts" server-side.
  const accountsParam =
    accountsSel && selected && accountsSel.length > 0 && accountsSel.length < selected.accounts.length
      ? accountsSel.map((a) => a.slug)
      : undefined;
  const emptyAccountSelection = accountsSel !== null && accountsSel.length === 0;

  const startMut = useMutation({
    mutationFn: () =>
      auxSyncApi.start({
        tour_operator_id: tenant!.id,
        integration_name: integration!,
        ...(accountsParam ? { accounts: accountsParam } : {}),
        from_date: fromDate || null,
        to_date: toDate || null,
        sample_limit: sampleLimit ? Number(sampleLimit) : null,
        use_ai: useAi,
        fetch_product_content: fetchContent,
      }),
    onSuccess: (r) => {
      show('Aux info sync started.', 'success');
      navigate(`/sync/aux-info/${r.id}/review`);
    },
    onError: (e) => show((e as Error).message, 'error'),
  });

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Aux Info Sync <HelpLink story="aux-sync" />
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
        Extracts the auxiliary data ops normally hand-collect during onboarding — package /
        question / resource / custom-field IDs, guide &amp; vehicle rosters, tag-mapping
        candidates — from the booking system&apos;s APIs plus a sample of real bookings. Everything
        is reviewed before anything is written, and re-running is always safe: records already
        applied come back as &quot;existing&quot;.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }} useFlexGap flexWrap="wrap">
        <Box sx={{ width: 280 }}>
          <TenantPicker
            placeholder="Choose a tour operator"
            value={tenant}
            onChange={(t) => {
              setTenant(t);
              setIntegration(null);
              setAccountsSel(null);
            }}
          />
        </Box>
        <TextField
          select
          size="small"
          label="Integration"
          value={integration ?? ''}
          onChange={(e) => {
            setIntegration(e.target.value || null);
            setAccountsSel(null);
          }}
          disabled={!tenant || contextQuery.isLoading}
          helperText={
            tenant && context && integrations.length === 0
              ? 'No configured booking integration — set up its credentials first'
              : undefined
          }
          sx={{ minWidth: 240 }}
        >
          {integrations.map((i) => (
            <MenuItem key={i.integration} value={i.integration}>
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{i.integration}</span>
                {i.supported === 'generic' && (
                  <Chip size="small" variant="outlined" label="generic sampling" />
                )}
              </Stack>
            </MenuItem>
          ))}
        </TextField>
        {/* Multi-account restechs: leave empty to sample every configured
            account, or narrow to a subset. Single-account setups have
            nothing to choose, so the control stays hidden. */}
        {selected && selected.accounts.length > 1 && (
          <Autocomplete
            multiple
            size="small"
            options={selected.accounts}
            value={accountsSel ?? selected.accounts}
            onChange={(_e, value) => setAccountsSel(value)}
            getOptionLabel={(a) => a.display_name}
            isOptionEqualToValue={(a, b) => (a.slug ?? null) === (b.slug ?? null)}
            disableCloseOnSelect
            sx={{ minWidth: 300 }}
            renderTags={(value, getTagProps) =>
              value.map((a, index) => (
                <Chip
                  size="small"
                  label={a.display_name}
                  {...getTagProps({ index })}
                  key={a.slug ?? '_default'}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Accounts"
                error={emptyAccountSelection}
                helperText={
                  emptyAccountSelection
                    ? 'Pick at least one account'
                    : accountsParam
                      ? `${accountsParam.length} of ${selected.accounts.length} accounts`
                      : `all ${selected.accounts.length} accounts`
                }
              />
            )}
          />
        )}
        <TextField
          size="small"
          type="date"
          label="From"
          InputLabelProps={{ shrink: true }}
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          helperText={!fromDate && !toDate ? `default: last 7 days` : undefined}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          InputLabelProps={{ shrink: true }}
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />
        <TextField
          size="small"
          type="number"
          label="Sample limit"
          value={sampleLimit}
          onChange={(e) => setSampleLimit(e.target.value)}
          helperText={`default ${context?.defaults.sample_limit ?? 500}`}
          sx={{ width: 130 }}
        />
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Tooltip title="AI pass over the sampled payloads: finds language sources, note-line sections and mapping candidates the deterministic scan misses. Every AI-proposed reference is re-verified against the sample before it becomes a record.">
          <FormControlLabel
            control={<Switch size="small" checked={useAi} onChange={(e) => setUseAi(e.target.checked)} />}
            label="AI analysis"
          />
        </Tooltip>
        <Tooltip title="Also fetch per-locale product content where the booking system provides it (multilingual redemption instructions land on product tags). Capped products × languages fan-out.">
          <FormControlLabel
            control={
              <Switch size="small" checked={fetchContent} onChange={(e) => setFetchContent(e.target.checked)} />
            }
            label="Fetch product content"
          />
        </Tooltip>
      </Stack>

      {selected && (
        <Alert severity={selected.supported === 'full' ? 'info' : 'warning'} sx={{ mb: 2, maxWidth: 760 }}>
          {selected.supported === 'full'
            ? `${selected.integration}: vendor-endpoint probes + booking sampling. `
            : `${selected.integration}: no dedicated collector yet — booking sampling only. `}
          Accounts covered:{' '}
          {(accountsSel ?? selected.accounts).map((a) => a.display_name).join(', ') || '—'}
          {accountsParam ? ` (${selected.accounts.length - accountsParam.length} skipped)` : ''}.
        </Alert>
      )}

      <Button
        variant="contained"
        disabled={!tenant || !integration || emptyAccountSelection || startMut.isPending}
        onClick={() => startMut.mutate()}
      >
        Start sync
      </Button>
    </Box>
  );
}
